import React, { useState, useEffect } from 'react';
import { Filter, X, Calendar, Search, Phone, Mail, User } from 'lucide-react';
import { cn } from '@/utils/cn';
import FSelect from '@/components/ui/FSelect';
import api from '@/utils/api';

interface FilterOption {
    opt_id: string | number;
    opt_name: string;
    uuid?: string;
}

interface ColumnConfig {
    col_id: string;
    col_name: string;
    col_label: string;
    col_type: string;
    col_required?: boolean;
    col_order?: number;
    options?: FilterOption[];
}

interface PipelineFiltersEnhancedProps {
    isOpen: boolean;
    onClose: () => void;
    columns: ColumnConfig[];
    onApplyFilters: (filters: any) => void;
    currentFilters?: any;
    isLoading?: boolean;
}

interface DispositionOption {
    option_id: number;
    value: string;
    display_order: number;
}

interface DispositionTree {
    l1Options: DispositionOption[];
    l2ByL1: Record<string, DispositionOption[]>;
    l3ByL2: Record<string, DispositionOption[]>;
}

// Columns excluded from "Additional Filters" section
const EXCLUDED_COLS = ['status', 'source'];

const PipelineFiltersEnhanced: React.FC<PipelineFiltersEnhancedProps> = ({
    isOpen,
    onClose,
    columns,
    onApplyFilters,
    currentFilters = {},
    isLoading = false
}) => {
    const [filters, setFilters] = useState<Record<string, any>>({});
    const [textFilters, setTextFilters] = useState({ name: '', phone: '', email: '' });
    const [dateFilters, setDateFilters] = useState<{
        created_date: { startDate?: string; endDate?: string };
        updated_date: { startDate?: string; endDate?: string };
    }>({ created_date: {}, updated_date: {} });

    // Last ABC Date
    const [lastAbcFrom, setLastAbcFrom] = useState('');
    const [lastAbcTo, setLastAbcTo] = useState('');

    // Last Call Status / Duration
    const [lastCallStatus, setLastCallStatus] = useState('');
    const [lastCallDurationMin, setLastCallDurationMin] = useState('');

    // Disposition
    const [dispositionTree, setDispositionTree] = useState<DispositionTree | null>(null);
    const [dispL1, setDispL1] = useState('');
    const [dispL2, setDispL2] = useState('');
    const [dispL3, setDispL3] = useState('');

    useEffect(() => {
        if (!isOpen) return;

        // Fetch disposition tree — api.req returns parsed JSON directly (res = {success, data, ...})
        api.req<any>({ url: '/dispositions/tree/abandoned_cart', method: api.Method.GET })
            .then(res => { if (res.success) setDispositionTree(res.data); })
            .catch(() => {});

        if (currentFilters) {
            const { name, phone, email, created_date, updated_date, last_abc_date, disposition_l1, disposition_l2, disposition_l3, last_call_status, last_call_duration_min, ...selectFilters } = currentFilters;
            setTextFilters({ name: name || '', phone: phone || '', email: email || '' });
            setDateFilters({ created_date: created_date || {}, updated_date: updated_date || {} });

            // Restore last ABC date
            if (last_abc_date && typeof last_abc_date === 'object') {
                setLastAbcFrom(last_abc_date.from || '');
                setLastAbcTo(last_abc_date.to || '');
            } else {
                setLastAbcFrom('');
                setLastAbcTo('');
            }

            // Restore call status / duration
            setLastCallStatus(last_call_status || '');
            setLastCallDurationMin(last_call_duration_min ? String(last_call_duration_min) : '');

            // Restore disposition
            setDispL1(disposition_l1 ? String(disposition_l1) : '');
            setDispL2(disposition_l2 ? String(disposition_l2) : '');
            setDispL3(disposition_l3 ? String(disposition_l3) : '');

            setFilters(selectFilters);
        }
    }, [isOpen, currentFilters]);

    const handleSelectFilter = (colName: string, values: any[]) => {
        setFilters(prev => ({ ...prev, [colName]: values.length > 0 ? values : undefined }));
    };

    const handleTextFilter = (field: 'name' | 'phone' | 'email', value: string) => {
        setTextFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleDateFilter = (type: 'created_date' | 'updated_date', field: 'startDate' | 'endDate', value: string) => {
        setDateFilters(prev => ({ ...prev, [type]: { ...prev[type], [field]: value } }));
    };

    const handleL1Change = (val: string) => { setDispL1(val); setDispL2(''); setDispL3(''); };

    const handleApply = () => {
        const allFilters: any = { ...filters };

        if (textFilters.name?.trim()) allFilters.name = textFilters.name.trim();
        if (textFilters.phone?.trim()) allFilters.phone = textFilters.phone.trim();
        if (textFilters.email?.trim()) allFilters.email = textFilters.email.trim();

        if (dateFilters.created_date.startDate || dateFilters.created_date.endDate)
            allFilters.created_date = dateFilters.created_date;
        if (dateFilters.updated_date.startDate || dateFilters.updated_date.endDate)
            allFilters.updated_date = dateFilters.updated_date;

        if (lastAbcFrom || lastAbcTo)
            allFilters.last_abc_date = { from: lastAbcFrom, to: lastAbcTo };

        if (lastCallStatus) allFilters.last_call_status = lastCallStatus;
        if (lastCallDurationMin && parseInt(lastCallDurationMin) > 0)
            allFilters.last_call_duration_min = parseInt(lastCallDurationMin);

        if (dispL1) allFilters.disposition_l1 = dispL1;
        if (dispL2) allFilters.disposition_l2 = dispL2;
        if (dispL3) allFilters.disposition_l3 = dispL3;

        Object.keys(allFilters).forEach(key => {
            if (allFilters[key] === undefined || allFilters[key] === null ||
                (Array.isArray(allFilters[key]) && allFilters[key].length === 0))
                delete allFilters[key];
        });

        onApplyFilters(allFilters);
        onClose();
    };

    const handleClear = () => {
        setFilters({});
        setTextFilters({ name: '', phone: '', email: '' });
        setDateFilters({ created_date: {}, updated_date: {} });
        setLastAbcFrom('');
        setLastAbcTo('');
        setLastCallStatus('');
        setLastCallDurationMin('');
        setDispL1(''); setDispL2(''); setDispL3('');
        onApplyFilters({});
        onClose();
    };

    const getActiveFilterCount = () => {
        let count = 0;
        Object.keys(filters).forEach(key => {
            if (filters[key] && Array.isArray(filters[key]) && filters[key].length > 0) count++;
        });
        if (textFilters.name) count++;
        if (textFilters.phone) count++;
        if (textFilters.email) count++;
        if (dateFilters.created_date.startDate || dateFilters.created_date.endDate) count++;
        if (dateFilters.updated_date.startDate || dateFilters.updated_date.endDate) count++;
        if (lastAbcFrom || lastAbcTo) count++;
        if (lastCallStatus) count++;
        if (lastCallDurationMin && parseInt(lastCallDurationMin) > 0) count++;
        if (dispL1) count++;
        return count;
    };

    if (!isOpen) return null;

    const sortedColumns = [...columns]
        .filter(col => !EXCLUDED_COLS.includes(col.col_name))
        .sort((a, b) => (a.col_order || 999) - (b.col_order || 999));

    const l2Options = dispL1 && dispositionTree ? (dispositionTree.l2ByL1[dispL1] || []) : [];
    const l3Options = dispL2 && dispositionTree ? (dispositionTree.l3ByL2[dispL2] || []) : [];

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose} />

            <div className="fixed right-0 top-0 h-full w-[600px] bg-white shadow-xl z-50 flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center gap-3">
                        <Filter className="h-5 w-5 text-gray-600" />
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Advanced Filters</h2>
                            <p className="text-xs text-gray-500 mt-0.5">Filter leads by multiple criteria</p>
                        </div>
                        {getActiveFilterCount() > 0 && (
                            <span className="px-2.5 py-1 bg-blue-100 text-brandColor text-xs rounded-full font-semibold">
                                {getActiveFilterCount()} active
                            </span>
                        )}
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-5 space-y-6">

                    {/* Phone */}
                    <div>
                        <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-1.5">
                            <Phone className="h-3.5 w-3.5" /> Phone
                        </label>
                        <input type="text" value={textFilters.phone} onChange={(e) => handleTextFilter('phone', e.target.value)} placeholder="Search by phone number..." className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brandColor" />
                    </div>

                    {/* Date Filters */}
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200 flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-gray-500" /> Date Filters
                        </h3>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                            <div>
                                <label className="text-xs font-medium text-gray-700 mb-1.5 block">Created Date From</label>
                                <input type="date" value={dateFilters.created_date.startDate || ''} onChange={(e) => handleDateFilter('created_date', 'startDate', e.target.value)} className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brandColor" />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-700 mb-1.5 block">Created Date To</label>
                                <input type="date" value={dateFilters.created_date.endDate || ''} onChange={(e) => handleDateFilter('created_date', 'endDate', e.target.value)} className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brandColor" />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-700 mb-1.5 block">Updated Date From</label>
                                <input type="date" value={dateFilters.updated_date.startDate || ''} onChange={(e) => handleDateFilter('updated_date', 'startDate', e.target.value)} className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brandColor" />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-700 mb-1.5 block">Updated Date To</label>
                                <input type="date" value={dateFilters.updated_date.endDate || ''} onChange={(e) => handleDateFilter('updated_date', 'endDate', e.target.value)} className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brandColor" />
                            </div>

                            {/* Last ABC Date */}
                            <div>
                                <label className="text-xs font-medium text-gray-700 mb-1.5 block">Last ABC Date From</label>
                                <input type="date" value={lastAbcFrom} onChange={(e) => setLastAbcFrom(e.target.value)} className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brandColor" />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-700 mb-1.5 block">Last ABC Date To</label>
                                <input type="date" value={lastAbcTo} onChange={(e) => setLastAbcTo(e.target.value)} className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brandColor" />
                            </div>
                        </div>
                    </div>

                    {/* Last Call Status & Duration */}
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200 flex items-center gap-2">
                            <Phone className="h-4 w-4 text-gray-500" /> Call Filters
                        </h3>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                            <div className="col-span-2">
                                <label className="text-xs font-medium text-gray-700 mb-1.5 block">Last Call Status</label>
                                <select value={lastCallStatus} onChange={(e) => setLastCallStatus(e.target.value)} className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brandColor">
                                    <option value="">Any</option>
                                    <option value="answered">Answered</option>
                                    <option value="missed">Missed</option>
                                    <option value="initiated">Initiated</option>
                                    <option value="no_calls">No Calls</option>
                                </select>
                            </div>
                            <div className="col-span-2">
                                <label className="text-xs font-medium text-gray-700 mb-1.5 block">Min Call Duration (seconds)</label>
                                <input type="number" min="0" value={lastCallDurationMin} onChange={(e) => setLastCallDurationMin(e.target.value)} placeholder="e.g. 60" className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brandColor" />
                            </div>
                        </div>
                    </div>

                    {/* Disposition */}
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200 flex items-center gap-2">
                            <Search className="h-4 w-4 text-gray-500" /> Disposition
                        </h3>
                        <div className="space-y-2">
                            <div>
                                <label className="text-xs font-medium text-gray-700 mb-1.5 block">Lead Status L1</label>
                                <select value={dispL1} onChange={(e) => handleL1Change(e.target.value)} className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brandColor">
                                    <option value="">Select L1 Status</option>
                                    {dispositionTree?.l1Options.map(opt => (
                                        <option key={opt.option_id} value={String(opt.option_id)}>{opt.value}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-700 mb-1.5 block">Lead Status L2 <span className="text-gray-400">(optional)</span></label>
                                <select value={dispL2} onChange={(e) => { setDispL2(e.target.value); setDispL3(''); }} disabled={!dispL1 || l2Options.length === 0} className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brandColor disabled:bg-gray-50 disabled:text-gray-400">
                                    <option value="">{!dispL1 ? 'Select L1 first' : 'All L2'}</option>
                                    {l2Options.map(opt => (
                                        <option key={opt.option_id} value={String(opt.option_id)}>{opt.value}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-700 mb-1.5 block">Lead Status L3 <span className="text-gray-400">(optional)</span></label>
                                <select value={dispL3} onChange={(e) => setDispL3(e.target.value)} disabled={!dispL2 || l3Options.length === 0} className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brandColor disabled:bg-gray-50 disabled:text-gray-400">
                                    <option value="">{!dispL2 ? 'Select L2 first' : 'All L3'}</option>
                                    {l3Options.map(opt => (
                                        <option key={opt.option_id} value={String(opt.option_id)}>{opt.value}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Primary Fields */}
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200 flex items-center gap-2">
                            <User className="h-4 w-4 text-gray-500" /> Primary Fields
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs font-medium text-gray-700 mb-1.5 block">Name</label>
                                <input type="text" value={textFilters.name} onChange={(e) => handleTextFilter('name', e.target.value)} placeholder="Search by name..." className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brandColor" />
                            </div>
                            <div>
                                <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-1.5">
                                    <Mail className="h-3.5 w-3.5" /> Email
                                </label>
                                <input type="email" value={textFilters.email} onChange={(e) => handleTextFilter('email', e.target.value)} placeholder="Search by email..." className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brandColor" />
                            </div>
                        </div>
                    </div>

                    {/* Additional select filters (status + source excluded) */}
                    {sortedColumns.filter(col => col.col_type === 'select' && col.options && col.options.length > 0).length > 0 && (
                        <div>
                            <h3 className="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">Additional Filters</h3>
                            <div className="grid grid-cols-2 gap-4">
                                {sortedColumns.filter(col => col.col_type === 'select' && col.options && col.options.length > 0).map((column) => (
                                    <div key={column.col_id}>
                                        <label className="text-xs font-medium text-gray-700 mb-1.5 block">
                                            {column.col_label}
                                            {filters[column.col_name]?.length > 0 && (
                                                <span className="ml-2 px-2 py-0.5 bg-blue-100 text-brandColor text-xs rounded-full">{filters[column.col_name].length}</span>
                                            )}
                                        </label>
                                        <FSelect
                                            options={column.options || []}
                                            value={filters[column.col_name] || []}
                                            onChange={(values) => handleSelectFilter(column.col_name, values)}
                                            placeholder={`Select ${column.col_label}...`}
                                            searchable={true}
                                            showCount={false}
                                            className="w-full"
                                            wideDropdown={true}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-5 border-t border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-gray-600">{getActiveFilterCount()} filter{getActiveFilterCount() !== 1 ? 's' : ''} selected</span>
                        {getActiveFilterCount() > 0 && (
                            <button onClick={handleClear} className="text-sm text-red-600 hover:text-red-700 font-medium">Clear All</button>
                        )}
                    </div>
                    <div className="flex gap-3">
                        <button onClick={onClose} className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
                        <button onClick={handleApply} disabled={isLoading} className={cn("flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors", isLoading ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-brandColor text-white hover:bg-brandColor shadow-sm")}>
                            {isLoading ? 'Applying...' : 'Apply Filters'}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PipelineFiltersEnhanced;
